import React, { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'
import { formatDate, formatTime } from '../utils/helpers'
import { CalendarIcon, MapPinIcon, ClockIcon, CheckCircleIcon, XCircleIcon, QuestionMarkCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const UserDashboard = () => {
  const { user, loading: authLoading } = useAuth()
  const [meetups, setMeetups] = useState({
    registered: [],
    declined: [],
    noResponse: []
  })
  const [pastMeetups, setPastMeetups] = useState([])
  const [pastCounts, setPastCounts] = useState({ registered: 0, declined: 0, noResponse: 0 })
  const [loading, setLoading] = useState(true)

  // Bumping reloadKey refetches after a change
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!user || authLoading) return

    const fetchMyMeetups = async () => {
      try {
        const response = await api.get('/meetups/my-meetups')
        const allMeetups = response.data

        // Separate upcoming and past events
        const now = new Date()
        const upcoming = {
          registered: [],
          declined: [],
          noResponse: []
        }
        const past = []
        const pastCounts = { registered: 0, declined: 0, noResponse: 0 }

        // Filter each category
        Object.keys(allMeetups).forEach(category => {
          allMeetups[category].forEach(meetup => {
            // Combine date and end time to determine if meetup is truly past
            const meetupDate = new Date(meetup.date)
            const [hours, minutes] = meetup.endTime.split(':')
            meetupDate.setHours(parseInt(hours), parseInt(minutes), 0, 0)

            if (meetupDate >= now) {
              upcoming[category].push(meetup)
            } else {
              past.push(meetup)
              if (category in pastCounts) pastCounts[category]++
            }
          })
        })

        // Sort past events by date, most recent first
        past.sort((a, b) => {
          const dateA = new Date(a.date)
          const dateB = new Date(b.date)
          return dateB - dateA
        })

        setMeetups(upcoming)
        setPastMeetups(past)
        setPastCounts(pastCounts)
      } catch (error) {
        console.error('Fout bij ophalen meetups:', error)
        toast.error('Kon meetups niet laden')
      } finally {
        setLoading(false)
      }
    }

    fetchMyMeetups()
  }, [user, authLoading, reloadKey])

  const handleRespond = async (meetupId, action) => {
    try {
      const response = await api.post(`/meetups/${meetupId}/respond`, { action })
      toast.success(response.data.message)
      // Refresh the meetup list
      setReloadKey(k => k + 1)
    } catch (error) {
      console.error('Fout bij reageren:', error)
      toast.error(error.response?.data?.error || 'Er is iets misgegaan')
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const MeetupCard = ({ meetup, status, isPast = false }) => {
    const statusConfig = {
      registered: {
        icon: CheckCircleIcon,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-gray-700',
        borderColor: 'border-green-200 dark:border-green-800',
        label: meetup.registrationStatus === 'waitlist' ? 'Wachtlijst' : 'Geregistreerd'
      },
      declined: {
        icon: XCircleIcon,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-gray-700',
        borderColor: 'border-red-200 dark:border-red-800',
        label: 'Afgemeld'
      },
      noResponse: {
        icon: QuestionMarkCircleIcon,
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-50 dark:bg-gray-700',
        borderColor: 'border-gray-200 dark:border-gray-600',
        label: 'Geen reactie'
      }
    }

    const config = statusConfig[status] || {
      icon: CalendarIcon,
      color: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-white dark:bg-gray-800',
      borderColor: 'border-gray-200 dark:border-gray-600',
      label: ''
    }
    const Icon = config.icon

    return (
      <div className={`card ${config.bgColor} ${config.borderColor} border hover:shadow-md transition-shadow`}>
        <div className="card-body">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
              {meetup.title}
            </h3>
            {!isPast && (
              <div className={`flex items-center space-x-1 ${config.color}`}>
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{config.label}</span>
              </div>
            )}
          </div>

          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4" />
              <span>{formatDate(meetup.date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-4 w-4" />
              <span>{formatTime(meetup.startTime)} - {formatTime(meetup.endTime)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPinIcon className="h-4 w-4" />
              <span>{meetup.location}</span>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-3">
            {/* Action buttons - only show for upcoming events */}
            {!isPast && (
              <div className="flex gap-2">
                {(status === 'noResponse' || status === 'declined') && (
                  <button
                    onClick={() => handleRespond(meetup.id, 'confirm')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {status === 'declined' ? 'Ik kom wel' : 'Ik kom'}
                  </button>
                )}
                {(status === 'noResponse' || status === 'registered') && (
                  <button
                    onClick={() => handleRespond(meetup.id, 'decline')}
                    className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {status === 'noResponse' ? 'Ik kom niet' : 'Ik kan toch niet'}
                  </button>
                )}
              </div>
            )}

            {/* ICS Download and Details links */}
            <div className="flex items-center justify-between">
              <Link
                to={`/meetup/${meetup.id}`}
                className={`${config.color} hover:underline text-sm font-medium`}
              >
                Meer details →
              </Link>
              {!isPast && (
                <a
                  href={`/api/meetups/${meetup.id}/ics`}
                  download
                  className={`${config.color} hover:underline text-sm font-medium flex items-center space-x-1`}
                  title="Download naar agenda"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Agenda</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welkom, {user.name}!
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Overzicht van al je meetups
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Meetups laden...</p>
          </div>
        ) : (
          <>

            {/* Desktop 3-column layout */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6">
              {/* Column 1: Registered */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    <span>Ingeschreven ({meetups.registered.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.registered.length > 0 ? (
                    meetups.registered.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="registered" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Nog geen nieuwe meetups gepland
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Column 2: Declined */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    <span>Afgemeld ({meetups.declined.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.declined.length > 0 ? (
                    meetups.declined.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="declined" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Geen afmeldingen
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Column 3: No Response */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <QuestionMarkCircleIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    <span>Geen reactie ({meetups.noResponse.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.noResponse.length > 0 ? (
                    meetups.noResponse.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="noResponse" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Je hebt op alle meetups gereageerd!
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile/Tablet: Accordion-style sections */}
            <div className="lg:hidden space-y-6">
              {/* Registered Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    <span>Ingeschreven ({meetups.registered.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.registered.length > 0 ? (
                    meetups.registered.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="registered" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Nog geen nieuwe meetups gepland
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Declined Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    <span>Afgemeld ({meetups.declined.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.declined.length > 0 ? (
                    meetups.declined.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="declined" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Geen afmeldingen
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* No Response Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <QuestionMarkCircleIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    <span>Geen reactie ({meetups.noResponse.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {meetups.noResponse.length > 0 ? (
                    meetups.noResponse.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} status="noResponse" />
                    ))
                  ) : (
                    <div className="card bg-gray-50 dark:bg-gray-800">
                      <div className="card-body text-center text-gray-500 dark:text-gray-400">
                        Je hebt op alle meetups gereageerd!
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Horizontal line separator */}
            <div className="my-12">
              <hr className="border-t-2 border-gray-300 dark:border-gray-700" />
            </div>

            {/* Past Events Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Voorbije Evenementen ({pastMeetups.length})
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Overzicht van evenementen die al hebben plaatsgevonden
              </p>
            </div>

            {/* Single list of all past events */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastMeetups.length > 0 ? (
                pastMeetups.map((meetup) => (
                  <MeetupCard key={meetup.id} meetup={meetup} isPast={true} />
                ))
              ) : (
                <div className="col-span-full">
                  <div className="card bg-gray-50 dark:bg-gray-800">
                    <div className="card-body text-center text-gray-500 dark:text-gray-400">
                      Geen voorbije evenementen
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default UserDashboard
